import { Component, OnInit, OnDestroy, ViewEncapsulation, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ExecuteService } from 'src/app/Services/execute.service';
import { TestAutomationService } from 'src/app/Services/test-automation.service';
import { NotifyService } from 'src/app/Services/notify.service';
import { Subscription, interval } from 'rxjs';
import { ExecutionConfigModel } from 'src/app/Models/execution-config-model';
import { environment } from 'src/environments/environment';
import { MatDialog, MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { ExecutionContainerComponent } from '../execution-container/execution-container.component';
import { TestAutomationCase } from 'src/app/Models/test-automation-case.model';
import { TestDataDialogComponent } from '../test-data-dialog/test-data-dialog.component';
import { DialogComponent, ConfirmDialogModel } from '../dialog/dialog.component';
import { Literals } from 'src/app/literals';
import { SuiteCanvasDialogComponent } from '../suite-canvas-component/suite-canvas-dialog.component';

// ─────────────────────────────────────────────────────────────────────────────
// HIERARCHY MODELS
// ─────────────────────────────────────────────────────────────────────────────

export interface HierarchyStep {
  step_id: string;
  step_name: string;
  path: string;
  globalOrder: number;
  selectedForAutomation: boolean;
}

export interface HierarchyScenario {
  scenarioName: string;
  scenario_id: string;
  steps: HierarchyStep[];
  expanded: boolean;
  selectedForAutomation?: boolean;
}

export interface HierarchyFeature {
  featureName: string;
  feature_id: string;
  scenarios: HierarchyScenario[];
  expanded: boolean;
  selectedForAutomation?: boolean;
}

export interface SuiteTree {
  suiteName: string;
  suite_id: string;
  features: HierarchyFeature[];
}

export interface StepItem {
  path: string;
  step_id: string;
  step_name: string;
  globalOrder: number;
  selected: boolean;
  suiteTree: SuiteTree;
}

interface AutomationArtifact {
  artifact_id: string;
  artifact_name: string;
  artifact_type: 'feature' | 'scenario' | 'step';
}

interface LockedAutomationContext {
  preArtifacts:  AutomationArtifact[];
  postArtifacts: AutomationArtifact[];
  mainStepNames: string[];
  mainStepIds:   string[];
  selectedFeatures:  string[];
  selectedScenarios: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// GENERATE TEST CASE COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'ze-generate-test-case',
  templateUrl: './generate-test-case.component.html',
  styleUrls: ['./generate-test-case.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class GenerateTestCaseComponent implements OnInit, OnDestroy {

  reportId: string;
  showStepsSection  = false;
  showTableSection  = false;
  testCases: any[]  = [];
  reportData: any   = null;
  testCaseSteps: StepItem[] = [];

  summaryText = '';
  isGenerating = false;
  loadingSubmessage = 'Please wait while we process your request';
  selectAll = false;
  showGenerateAutomationButton = true;

  searchText        = '';
  filteredTestCases: any[] = [];
  isSavingToLibrary = false;

  editingIndex: number | null = null;
  editableRow: any = {};

 
  private static readonly SS_CTX = 'ze_automation_ctx';
  private static readonly SS_RID = 'ze_locked_reportId';

  private _lockedCtx: LockedAutomationContext = this._emptyCtx();

  private _emptyCtx(): LockedAutomationContext {
    return { preArtifacts: [], postArtifacts: [], mainStepNames: [], mainStepIds: [], selectedFeatures: [], selectedScenarios: [] };
  }

  private _setLockedCtx(ctx: LockedAutomationContext): void {
    this._lockedCtx = ctx;
    try { sessionStorage.setItem(GenerateTestCaseComponent.SS_CTX, JSON.stringify(ctx)); } catch {}
  }

  private _getLockedCtx(): LockedAutomationContext {
    if (this._lockedCtx.preArtifacts.length || this._lockedCtx.postArtifacts.length) { return this._lockedCtx; }
    try {
      const s = sessionStorage.getItem(GenerateTestCaseComponent.SS_CTX);
      return s ? JSON.parse(s) : this._emptyCtx();
    } catch { return this._emptyCtx(); }
  }


  private _initLockedCtx(): void {
    try {
      const storedRid = sessionStorage.getItem(GenerateTestCaseComponent.SS_RID) || '';
      if (!storedRid || storedRid === this.reportId) {
        const s = sessionStorage.getItem(GenerateTestCaseComponent.SS_CTX);
        this._lockedCtx = s ? JSON.parse(s) : this._emptyCtx();
      } else { this._clearLockedCtx(); }
    } catch { this._lockedCtx = this._emptyCtx(); }
  }

  private _clearLockedCtx(): void {
    this._lockedCtx = this._emptyCtx();
    try { sessionStorage.removeItem(GenerateTestCaseComponent.SS_CTX); sessionStorage.removeItem(GenerateTestCaseComponent.SS_RID); } catch {}
  }

  private loadingStartTime = 0;
  private messageIntervalSubscription: Subscription;
  private pollingSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private executeService: ExecuteService,
    private testautomationService: TestAutomationService,
    private notifyService: NotifyService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.reportId = this.route.snapshot.paramMap.get('reportId');
    try { sessionStorage.setItem(GenerateTestCaseComponent.SS_RID, this.reportId || ''); } catch {}
    this._initLockedCtx();
    this.isTestCaseGenerated();
  }

  ngOnDestroy() {
    this.stopLoadingMessages();
    this.stopPolling();
  }

  isTestCaseGenerated() {
    this.executeService.isTestCaseGenerated(this.reportId).subscribe((res: any) => {
      if (res.data && res.data.test_cases && res.data.test_cases.length > 0) {
        this.showStepsSection = false;
        this.showTableSection = true;
        this.showGenerateAutomationButton = false;
        this.testCases = res.data.test_cases.map((tc: any) => ({
          ...tc,
          main_step:           tc.main_step || (tc.main_steps && tc.main_steps.length > 0 ? tc.main_steps[0] : ''),
          selected:            tc.selected  || false,
          xpeer_command_steps: tc.xpeer_command_steps || [],
          pre_step_artifacts:  tc.pre_step_artifacts  || [],
          post_step_artifacts: tc.post_step_artifacts || [],
          pre_step_count:      tc.pre_step_count      || 0,
          main_step_count:     tc.main_step_count     || 0,
          post_step_count:     tc.post_step_count     || 0,
        }));
        this.updateFilteredList();
        if (this.testCaseSteps.length === 0) {
          this.executeService.getReportById(this.reportId).subscribe((r: any) => {
            this.reportData = r.data;
            this.buildHierarchyFromReport();
          });
        }
      } else {
        this.executeService.getReportById(this.reportId).subscribe((res: any) => {
          if (res && res.data && res.data.test_case_generation_status === 'COMPLETED') {
            this.stopPolling(); this.stopLoadingMessages(); this.isGenerating = false; this.generatedTestCase();
          } else if (res && res.data && res.data.test_case_generation_status === 'IN_PROGRESS') {
            this.showGenerateAutomationButton = false; this.isGenerating = true; this.startPolling();
          } else {
            this.reportData = res.data; this.showStepsSection = true; this.showTableSection = false; this.buildHierarchyFromReport();
          }
        });
      }
    });
  }

  generatedTestCase() {
    this.testautomationService.generatedTestCase(this.reportId).subscribe((res: any) => {
      if (res && res.data && Array.isArray(res.data) && res.data.length > 0) {
        this.testCases = res.data.map((tc: any) => {
          const testCaseId = tc._id && tc._id.$oid ? tc._id.$oid : (tc._id || null);
          return {
            _id: testCaseId,
            test_case_id: tc.test_case_id || '',
            testcase_id: tc.test_case_id || '',
            test_case: tc.description || '',
            description: tc.description || '',
            main_steps: [],
            main_step: tc.description || '',
            pre_step_artifacts: tc.pre_step_artifacts || [],
            post_step_artifacts: tc.post_step_artifacts || [],
            test_steps: tc.test_steps || [],
            test_data: tc.test_data || [],
            expected_result: tc.expected_result || '',
            suite_name: '',
            suite_id: '',
            selected: false,
            xpeer_command_steps: tc.xpeer_command_steps || [],
            pre_step_count: tc.pre_step_count || 0,
            main_step_count: tc.main_step_count || 0,
            post_step_count: tc.post_step_count || 0,
            last_run_report_id: tc.last_run_report_id || null,
          };
        });
        this.showStepsSection = false; this.showTableSection = true; this.showGenerateAutomationButton = true;
        this.updateFilteredList();
        if (this.testCaseSteps.length === 0) {
          this.executeService.getReportById(this.reportId).subscribe((r: any) => { this.reportData = r.data; this.buildHierarchyFromReport(); });
        }
      } else if (res && res.operationStatus === 'ERROR') {
        this.notifyService.onError('Error', res.operationMessage || 'Failed to fetch generated test cases.');
      } else {
        this.showStepsSection = true; this.showTableSection = false; this.showGenerateAutomationButton = true;
        this.notifyService.onWarning('Info', 'No generated test cases found.');
      }
    }, () => {
      this.notifyService.onError('Error', 'An issue occurred while fetching generated test cases. Please try again.');
    });
  }

  openArtifactInCanvas(tc: any): void {
    if (!tc.suite_id) { this.notifyService.onWarning('Warning', 'No artifact ID found for this test case.'); return; }
    this.dialog.open(SuiteCanvasDialogComponent, {
      data: { suiteId: tc.suite_id, suiteName: tc.suite_name || tc.suite_id },
      width: '85vw', maxWidth: '85vw', height: '85vh',
      panelClass: 'suite-canvas-dialog-panel', backdropClass: 'blur-dialog-backdrop',
    }).afterClosed().subscribe();
  }


  buildHierarchyFromReport() {
    
    
    
    const reportLinks = this.reportData && this.reportData.report_links;
    const liveStatus_obj = reportLinks && reportLinks.live_status;
    const chromeStatus = liveStatus_obj && liveStatus_obj.Chrome;
    

    let liveStatus: any = null;
    if (chromeStatus) {
      liveStatus = chromeStatus['1'] || chromeStatus['0'];
      if (!liveStatus) {
        const keys = Object.keys(chromeStatus);
        if (keys.length > 0) { liveStatus = chromeStatus[keys[0]]; }
      }
    }
    
    // ... rest unchanged
    const allSteps: { path: string; step_id: string; step_name: string }[] = [];
    function collectLeafSteps(node: any, pathParts: string[]) {
      const parts = pathParts.slice();
      if (node.name) { parts.push(node.name); }
      const children = node.structure;
      const hasChildren = children && Array.isArray(children) && children.length > 0;
      if (hasChildren) { for (const child of children) { collectLeafSteps(child, parts); } }
      else {
        const id = (node._id || '').toString().trim();
        if (id) { allSteps.push({ path: parts.join(' > '), step_id: id, step_name: (node.name || '').trim() }); }
        else { console.warn('[collectLeafSteps] Skipping node with missing _id:', node); }
      }
    }

    collectLeafSteps(liveStatus, []);
    if (allSteps.length === 0) { this.testCaseSteps = []; return; }

    const suiteTree = this._buildSuiteTree(liveStatus, allSteps);
    this.testCaseSteps = allSteps.map((step, idx) => ({
      path: step.path, step_id: step.step_id, step_name: step.step_name,
      globalOrder: idx, selected: false, suiteTree: this._cloneSuiteTree(suiteTree),
    }));
  }

  private _buildSuiteTree(liveStatus: any, allSteps: { path: string; step_id: string; step_name: string }[]): SuiteTree {
    const suiteName = liveStatus.name || 'Suite';
    const suite_id  = liveStatus._id  || '';
    const features: HierarchyFeature[] = [];
    let globalOrder = 0;

    const tryBuildFromNodes = (node: any): boolean => {
      if (!node.structure || !node.structure.length) { return false; }
      return node.structure.some((c: any) => c._type === 'feature' || (c.structure && c.structure.length));
    };

    if (tryBuildFromNodes(liveStatus)) {
      for (const featureNode of liveStatus.structure || []) {
        const scenarios: HierarchyScenario[] = [];
        for (const scenarioNode of (featureNode.structure || [])) {
          const steps: HierarchyStep[] = [];
          for (const stepNode of (scenarioNode.structure && scenarioNode.structure.length ? scenarioNode.structure : [scenarioNode])) {
            if (stepNode._id) {
              steps.push({ step_id: stepNode._id, step_name: (stepNode.name || '').trim(), path: stepNode.name || '', globalOrder: globalOrder++, selectedForAutomation: false });
            }
          }
          if (steps.length) {
            scenarios.push({ scenarioName: scenarioNode.name || `Scenario ${scenarios.length + 1}`, scenario_id: scenarioNode._id || `sc_${scenarios.length}`, steps, expanded: true, _nodeType: scenarioNode._type || 'scenario' } as HierarchyScenario);
          }
        }
        if (scenarios.length) {
          features.push({ featureName: featureNode.name || `Feature ${features.length + 1}`, feature_id: featureNode._id || `ft_${features.length}`, scenarios, expanded: true, _nodeType: featureNode._type || 'feature' } as HierarchyFeature);
        }
      }
    }

    if (!features.length && allSteps.length) {
      features.push({
        featureName: suiteName, feature_id: suite_id || 'ft_0',
        scenarios: [{ scenarioName: 'All Steps', scenario_id: 'sc_0', expanded: true, steps: allSteps.map((s, i) => ({ step_id: s.step_id, step_name: s.step_name, path: s.path, globalOrder: i, selectedForAutomation: false })) }],
        expanded: true,
      });
    }
    return { suiteName, suite_id, features };
  }

  private _cloneSuiteTree(tree: SuiteTree): SuiteTree { return JSON.parse(JSON.stringify(tree)); }

  isStepBeforeMainStep(s: HierarchyStep, mainStep: StepItem): boolean { return s.globalOrder < mainStep.globalOrder; }
  isStepAfterMainStep(s: HierarchyStep, mainStep: StepItem): boolean  { return s.globalOrder > mainStep.globalOrder; }
  isScenarioBeforeStep(scenario: HierarchyScenario, mainStep: StepItem): boolean { return scenario.steps.every(s => s.globalOrder < mainStep.globalOrder); }
  isScenarioAfterStep(scenario: HierarchyScenario, mainStep: StepItem): boolean  { return scenario.steps.every(s => s.globalOrder > mainStep.globalOrder); }

  isFeatureBeforeStep(feature: HierarchyFeature, mainStep: StepItem): boolean {
    const all = feature.scenarios.reduce((a: HierarchyStep[], sc) => a.concat(sc.steps), []);
    return all.length > 0 && all.every(s => s.globalOrder < mainStep.globalOrder);
  }
  isFeatureAfterStep(feature: HierarchyFeature, mainStep: StepItem): boolean {
    const all = feature.scenarios.reduce((a: HierarchyStep[], sc) => a.concat(sc.steps), []);
    return all.length > 0 && all.every(s => s.globalOrder > mainStep.globalOrder);
  }
  isFeatureMixed(feature: HierarchyFeature, mainStep: StepItem): boolean {
    return !this.isFeatureBeforeStep(feature, mainStep) && !this.isFeatureAfterStep(feature, mainStep);
  }

  isFeatureFullySelected(feature: HierarchyFeature): boolean {
    const steps = feature.scenarios.reduce((a: HierarchyStep[], sc) => a.concat(sc.steps), []).filter(s => !this._isMainStepInTree(s));
    return steps.length > 0 && steps.every(s => s.selectedForAutomation);
  }
  isFeaturePartiallySelected(feature: HierarchyFeature): boolean {
    const all = feature.scenarios.reduce((a: HierarchyStep[], sc) => a.concat(sc.steps), []);
    const n   = all.filter(s => s.selectedForAutomation).length;
    return n > 0 && n < all.length;
  }
  isScenarioMixed(scenario: HierarchyScenario, mainStep: StepItem): boolean {
    return !this.isScenarioBeforeStep(scenario, mainStep) && !this.isScenarioAfterStep(scenario, mainStep);
}
  isScenarioFullySelected(scenario: HierarchyScenario): boolean {
    const steps = scenario.steps.filter(s => !this._isMainStepInTree(s));
    return steps.length > 0 && steps.every(s => s.selectedForAutomation);
  }
  isScenarioPartiallySelected(scenario: HierarchyScenario): boolean {
    const steps = scenario.steps.filter(s => !this._isMainStepInTree(s));
    const n     = steps.filter(s => s.selectedForAutomation).length;
    return n > 0 && n < steps.length;
  }

  getPreContextCount(mainStep: StepItem): number {
    return mainStep.suiteTree.features.reduce((a: HierarchyStep[], f) => a.concat(f.scenarios.reduce((b: HierarchyStep[], sc) => b.concat(sc.steps), [])), [])
      .filter(s => s.selectedForAutomation && s.globalOrder < mainStep.globalOrder).length;
  }
  getPostContextCount(mainStep: StepItem): number {
    return mainStep.suiteTree.features.reduce((a: HierarchyStep[], f) => a.concat(f.scenarios.reduce((b: HierarchyStep[], sc) => b.concat(sc.steps), [])), [])
      .filter(s => s.selectedForAutomation && s.globalOrder > mainStep.globalOrder).length;
  }

  private _isMainStepInTree(s: HierarchyStep, mainStep?: StepItem): boolean {
    if (!mainStep) { return false; }
    return s.step_id === mainStep.step_id;
  }

  toggleFeatureExpand(feature: HierarchyFeature): void  { feature.expanded  = !feature.expanded; }
  toggleScenarioExpand(scenario: HierarchyScenario): void { scenario.expanded = !scenario.expanded; }

  onFeatureToggle(feature: HierarchyFeature, mainStep: StepItem): void {
    feature.selectedForAutomation = !feature.selectedForAutomation;
    for (const scenario of feature.scenarios) {
      scenario.selectedForAutomation = feature.selectedForAutomation;
      for (const s of scenario.steps) { if (s.step_id !== mainStep.step_id) { s.selectedForAutomation = feature.selectedForAutomation; } }
    }
  }

  onScenarioToggle(scenario: HierarchyScenario, _feature: HierarchyFeature, mainStep: StepItem): void {
    scenario.selectedForAutomation = !scenario.selectedForAutomation;
    for (const s of scenario.steps) { if (s.step_id !== mainStep.step_id) { s.selectedForAutomation = scenario.selectedForAutomation; } }
  }

  onStepToggle(_s: HierarchyStep, _scenario: HierarchyScenario, _feature: HierarchyFeature, _mainStep: StepItem): void {}

  selectAllInTree(mainStep: StepItem): void {
    mainStep.suiteTree.features.reduce((a: HierarchyStep[], f) => a.concat(f.scenarios.reduce((b: HierarchyStep[], sc) => b.concat(sc.steps), [])), [])
      .forEach(s => { if (s.step_id !== mainStep.step_id) { s.selectedForAutomation = true; } });
  }
  clearAllInTree(mainStep: StepItem): void {
    mainStep.suiteTree.features.reduce((a: HierarchyStep[], f) => a.concat(f.scenarios.reduce((b: HierarchyStep[], sc) => b.concat(sc.steps), [])), [])
      .forEach(s => { s.selectedForAutomation = false; });
  }

  onStepSelectionChange(step: StepItem): void { if (!step.selected) { this.clearAllInTree(step); } }
  hasSelectedSteps(): boolean { return this.testCaseSteps.some(s => s.selected); }

  private _isRealMongoId(id: string): boolean { return !!id && /^[a-f0-9]{24}$/i.test(id); }

  private _snapshotAutomationContext(): LockedAutomationContext {
    const preArtifacts: AutomationArtifact[] = [], postArtifacts: AutomationArtifact[] = [];
    const selectedFeatures: string[] = [], selectedScenarios: string[] = [];

    const pushArtifact = (artifact: AutomationArtifact, refOrder: number, mainStep: StepItem) => {
      (refOrder < mainStep.globalOrder ? preArtifacts : postArtifacts).push(artifact);
    };

    const selectedMainSteps = this.testCaseSteps.filter(s => s.selected && s.step_id && this._isRealMongoId(s.step_id));

    for (const mainStep of selectedMainSteps) {
      for (const feature of mainStep.suiteTree.features) {
        const allFeatureSteps: HierarchyStep[] = feature.scenarios.reduce((a: HierarchyStep[], sc) => a.concat(sc.steps), []);
        const featureContainsMainStep = allFeatureSteps.some(s => s.step_id === mainStep.step_id);

        if ((feature as any).selectedForAutomation) {
          selectedFeatures.push(feature.featureName);
          if (!featureContainsMainStep) {
            if (this._isRealMongoId(feature.feature_id)) {
              pushArtifact({ artifact_id: feature.feature_id, artifact_name: feature.featureName, artifact_type: (feature as any)._nodeType || 'feature' }, (allFeatureSteps[0] ? allFeatureSteps[0].globalOrder : -1), mainStep);
            }
          } else {
            for (const scenario of feature.scenarios) {
              const scenContainsMain = scenario.steps.some(s => s.step_id === mainStep.step_id);
              if (!scenContainsMain) {
                if (this._isRealMongoId(scenario.scenario_id)) {
                  pushArtifact({ artifact_id: scenario.scenario_id, artifact_name: scenario.scenarioName, artifact_type: 'scenario' }, (scenario.steps[0] ? scenario.steps[0].globalOrder : -1), mainStep);
                }
              } else {
                for (const s of scenario.steps) {
                  if (s.step_id === mainStep.step_id || !this._isRealMongoId(s.step_id)) { continue; }
                  pushArtifact({ artifact_id: s.step_id, artifact_name: s.step_name, artifact_type: 'step' }, s.globalOrder, mainStep);
                }
              }
            }
          }
          continue;
        }

        for (const scenario of feature.scenarios) {
          const scenarioContainsMainStep = scenario.steps.some(s => s.step_id === mainStep.step_id);
          if ((scenario as any).selectedForAutomation) {
            selectedScenarios.push(scenario.scenarioName);
            if (!scenarioContainsMainStep) {
              if (this._isRealMongoId(scenario.scenario_id)) {
                pushArtifact({ artifact_id: scenario.scenario_id, artifact_name: scenario.scenarioName, artifact_type: 'scenario' }, (scenario.steps[0] ? scenario.steps[0].globalOrder : -1), mainStep);
              }
            } else {
              for (const s of scenario.steps) {
                if (s.step_id === mainStep.step_id || !this._isRealMongoId(s.step_id)) { continue; }
                pushArtifact({ artifact_id: s.step_id, artifact_name: s.step_name, artifact_type: 'step' }, s.globalOrder, mainStep);
              }
            }
            continue;
          }
          for (const step of scenario.steps) {
            if (!step.selectedForAutomation || step.step_id === mainStep.step_id || !this._isRealMongoId(step.step_id)) { continue; }
            pushArtifact({ artifact_id: step.step_id, artifact_name: step.step_name, artifact_type: 'step' }, step.globalOrder, mainStep);
          }
        }
      }
    }
    return { preArtifacts, postArtifacts, mainStepNames: selectedMainSteps.map(s => s.step_name), mainStepIds: selectedMainSteps.map(s => s.step_id), selectedFeatures, selectedScenarios };
  }

  generateTestCases() {
    const selectedSteps = this.testCaseSteps.filter(s => s.selected);
    const invalidSteps  = selectedSteps.filter(s => !s.step_id || s.step_id.trim() === '');
    if (invalidSteps.length > 0) { this.notifyService.onError('Error', 'One or more selected steps have no valid ID. Please refresh and try again.'); return; }

    const ctx = this._snapshotAutomationContext();
    this._setLockedCtx(ctx);
    try { sessionStorage.setItem(GenerateTestCaseComponent.SS_RID, this.reportId || ''); } catch {}

    const validMainStepIds = ctx.mainStepIds.filter(id => !!id && id.trim() !== '');
    if (validMainStepIds.length === 0) { this.notifyService.onError('Error', 'No valid step IDs could be resolved. Please re-select your steps.'); return; }

    const payload = {
      execution_id: this.reportId, summary: this.summaryText || '',
      steps: validMainStepIds.map(stepId => ({ step_id: stepId, pre_step_artifacts: ctx.preArtifacts, post_step_artifacts: ctx.postArtifacts })),
    };

    this.isGenerating = true; this.loadingStartTime = Date.now();
    this.loadingSubmessage = 'Please wait while we process your request';
    this.startLoadingMessages();

    this.testautomationService.generateTestCases(payload).subscribe(
      (res: any) => {
        if (res && res.data && res.data.status === 'IN_PROGRESS') { this.startPolling(); return; }
        this.stopLoadingMessages(); this.isGenerating = false;
        if (res && res.operationStatus === 'ERROR') { this.notifyService.onError('Error', res.operationMessage || 'An issue occurred while generating test cases. Please try again.'); return; }
        const testCasesArray = (res && res.data && res.data.test_cases) ? res.data.test_cases : (res && res.test_cases ? res.test_cases : null);
        if (testCasesArray && testCasesArray.length > 0) {
          this.testCases = testCasesArray.map((tc: any) => ({
            test_case_id: tc.test_case_id, testcase_id: tc.testcase_id, test_case: tc.test_case,
            main_steps: tc.main_steps || [], main_step: tc.main_step || (tc.main_steps && tc.main_steps[0] ? tc.main_steps[0] : ''),
            test_steps: tc.test_steps || [], test_data: tc.test_data || [],
            pre_step_artifacts: tc.pre_step_artifacts || [], post_step_artifacts: tc.post_step_artifacts || [],
            expected_result: tc.expected_result, suite_name: tc.suite_name, selected: false,
          }));
          this.showStepsSection = false; this.showTableSection = true; this.showGenerateAutomationButton = false;
          this.updateFilteredList();
        } else { this.isTestCaseGenerated(); }
      },
      () => { this.stopLoadingMessages(); this.stopPolling(); this.isGenerating = false; this.notifyService.onError('Error', 'An issue occurred while generating test cases. Please try again.'); },
    );
  }

  generateAutomation() {
    const projectId = localStorage.getItem('projectIdDb2');
    if (!projectId) { this.notifyService.onError('Error', 'Project ID not found. Please select a project first.'); return; }
    const selectedTestCases = this.testCases.filter(tc => tc.selected);
    if (selectedTestCases.length === 0) { this.notifyService.onWarning('Warning', 'Please select at least one test case to generate automation.'); return; }
    const ctx = this._getLockedCtx();
    if (ctx.postArtifacts.length > 0 && ctx.preArtifacts.length === 0) { this.notifyService.onWarning('Warning', 'POST artifacts selected without PRE artifacts. Automation may fail.'); }
    const payload = this._buildAutomationPayload(projectId, ctx, selectedTestCases);
    this.testautomationService.generateTestCaseAutomation(payload).subscribe(
      (res: any) => {
        if (res && res.operationStatus === 'ERROR') { this.notifyService.onError('Error', 'An issue occurred while generating automation. Please try again.'); }
        else { this.notifyService.notify('success', 'Success', 'Automation generated successfully.'); this._clearLockedCtx(); this.isTestCaseGenerated(); }
      },
      () => { this.notifyService.onError('Error', 'An issue occurred while generating automation. Please try again.'); },
    );
  }

  private _buildAutomationPayload(projectId: string, ctx: LockedAutomationContext, selectedTestCases: any[]): any {
    const testcaseIds: string[] = selectedTestCases.map(tc => tc.test_case_id || tc.testcase_id || this.extractTestCaseId(tc._id)).filter((id): id is string => !!id);
    return {
      execution_id: this.reportId, project_id: projectId, testcaseIds,
      include_pre_steps: ctx.preArtifacts.length > 0, include_post_steps: ctx.postArtifacts.length > 0,
      pre_step_artifacts: ctx.preArtifacts, post_step_artifacts: ctx.postArtifacts,
      selected_features: ctx.selectedFeatures, selected_scenarios: ctx.selectedScenarios,
      automation_context_tree: { selected_features: ctx.selectedFeatures, selected_scenarios: ctx.selectedScenarios, pre_step_names: ctx.preArtifacts, post_step_names: ctx.postArtifacts },
      test_cases: selectedTestCases.map((tc: any) => {
        const mainStep = tc.main_step || (tc.main_steps && tc.main_steps[0] ? tc.main_steps[0] : '');
        return { test_case_id: tc.test_case_id || tc.testcase_id || '', testcase_id: tc.testcase_id || tc.test_case_id || '', main_step: mainStep, pre_step_artifacts: tc.pre_step_artifacts || [], post_step_artifacts: tc.post_step_artifacts || [], test_steps: tc.test_steps || [], test_data: tc.test_data || [], expected_result: tc.expected_result || '', test_case_name: mainStep };
      }),
    };
  }

  startPolling() {
    this.stopPolling();
    this.pollingSubscription = interval(3000).subscribe(() => {
      if (!this.isGenerating || !this.reportId) { this.stopPolling(); return; }
      this.executeService.getReportById(this.reportId).subscribe(
        (res: any) => {
          const status = res && res.data ? res.data.test_case_generation_status : undefined;
          const triggerTime = res && res.data ? res.data.test_case_generation_triggered_at : undefined;
          if (status === 'COMPLETED') { this.stopPolling(); this.stopLoadingMessages(); this.isGenerating = false; this.generatedTestCase(); }
          else if (status === 'IN_PROGRESS') {
            if (triggerTime) { const ms = triggerTime < 1e12 ? triggerTime * 1000 : triggerTime; if (Date.now() - ms > 30 * 60 * 1000) { this.stopPolling(); this.stopLoadingMessages(); this.isGenerating = false; this.notifyService.onError('Timeout', 'Test case generation timed out.'); } }
          } else if (status === 'FAILED' || status === 'ERROR') { this.stopPolling(); this.stopLoadingMessages(); this.isGenerating = false; this.notifyService.onError('Error', 'Test case generation failed. Please try again.'); }
          else if (res && res.operationStatus === 'ERROR') { this.stopPolling(); this.stopLoadingMessages(); this.isGenerating = false; this.notifyService.onError('Error', 'An issue occurred. Please try again.'); }
        }, () => {},
      );
    });
  }

  stopPolling() { if (this.pollingSubscription) { this.pollingSubscription.unsubscribe(); this.pollingSubscription = null; } }

  startLoadingMessages() {
    this.stopLoadingMessages();
    this.messageIntervalSubscription = interval(5000).subscribe(() => {
      if (!this.isGenerating) { this.stopLoadingMessages(); return; }
      this.updateLoadingMessage(Math.floor((Date.now() - this.loadingStartTime) / 1000));
    });
  }
  stopLoadingMessages() { if (this.messageIntervalSubscription) { this.messageIntervalSubscription.unsubscribe(); this.messageIntervalSubscription = null; } }
  updateLoadingMessage(elapsedSeconds: number) {
    if      (elapsedSeconds < 10) { this.loadingSubmessage = 'Please wait while we process your request'; }
    else if (elapsedSeconds < 20) { this.loadingSubmessage = 'Analyzing selected steps and preparing test cases...'; }
    else if (elapsedSeconds < 30) { this.loadingSubmessage = 'AI is generating comprehensive test scenarios...'; }
    else if (elapsedSeconds < 45) { this.loadingSubmessage = 'Processing test data and expected results...'; }
    else if (elapsedSeconds < 60) { this.loadingSubmessage = 'Finalizing test cases and validating content...'; }
    else                          { this.loadingSubmessage = 'This is taking longer than usual. Please bear with us...'; }
  }

  runAll() {
    const selectedTestCases = this.testCases.filter(tc => tc.selected);
    if (selectedTestCases.length === 0) { this.notifyService.onWarning('Warning', 'Please select at least one test case to run.'); return; }
    this.dialog.open(DialogComponent, { data: new ConfirmDialogModel(`Run Selected (${selectedTestCases.length})`, `Run ${selectedTestCases.length} selected test case(s)?`) }).afterClosed().subscribe(result => {
      if (!result) { return; }
      const ids = selectedTestCases.map(tc => this.extractTestCaseId(tc._id || tc.testcase_id || tc.test_case_id)).filter((id): id is string => id !== null);
      if (!ids.length) { this.notifyService.onWarning('Warning', 'No valid test case IDs found for execution.'); return; }
      this.testautomationService.runSelectedTestCases(null, ids, true).subscribe({
        next: (res: any) => { if (res && res.operationStatus === 'SUCCESS') { this.notifyService.notify('success', 'Triggered', 'Selected testcases triggered.'); this.router.navigate(['/report'], { queryParams: { openTab: 'canvas' } }); } else { this.notifyService.onError('Error', 'Could not trigger selected testcases.'); } },
        error: () => this.notifyService.onError('Error', 'Error running selected testcases.'),
      });
    });
  }

  runSingle(testCase: any) {
    testCase.selected = true;
    if (!testCase.suite_name) { this.notifyService.onWarning('Warning', 'Test case does not have an artifact. Cannot run.'); return; }
    const originalSelections = this.testCases.map(tc => tc.selected);
    this.testCases.forEach(tc => tc.selected = false);
    testCase.selected = true;
    this.runTest(testCase);
    setTimeout(() => { this.testCases.forEach((tc, i) => tc.selected = originalSelections[i]); this.selectAll = this.testCases.length > 0 && this.testCases.every(tc => tc.selected); }, 100);
  }

  runTest(row: TestAutomationCase) {
    const executionConfig = new ExecutionConfigModel();
    executionConfig.resolution = 'default_resolution'; executionConfig.browser = ['Chrome'];
    executionConfig.default_pause = '0'; executionConfig.screenshot_required = false;
    executionConfig.default_hub = true; executionConfig.local_execution = false;
    executionConfig.hub_ip = environment.selenoidHubIp; executionConfig.port = environment.selenoidHubPort;
    this.dialog.open(ExecutionContainerComponent, {
      data: { executionConfig: row.execution_config || executionConfig, testCaseId: this.extractTestCaseId(row._id), datasetLink: row.dataset_link || '', dropped_suite: { _type: 'Suite', name: row.suite_id, _id: row.suite_id }, isFromGenerateTestCase: true, xpeer_command_steps: row.xpeer_command_steps || row.command_steps || [], pre_step_count: row.pre_step_count || 0, main_step_count: row.main_step_count || 0, post_step_count: row.post_step_count || 0 },
      backdropClass: 'blur-dialog-backdrop',
    }).afterClosed().subscribe();
  }

  toggleSelectAll() { this.selectAll = !this.selectAll; this.testCases.forEach(tc => tc.selected = this.selectAll); }
  onCheckboxChange() { this.selectAll = this.testCases.length > 0 && this.testCases.every(tc => tc.selected); }

  deleteSingleTestCase(testCase: any, index: number): void {
    testCase.selected = true;
    this.dialog.open(DialogComponent, { data: new ConfirmDialogModel('Delete Test Case', 'Are you sure you want to delete this test case?') }).afterClosed().subscribe(result => {
      if (!result) { this.notifyService.notify('warning', 'Aborted', 'Deletion aborted by user.'); return; }
      const testCaseId = this.extractTestCaseId(testCase._id);
      if (!testCaseId) { this.notifyService.onWarning('Warning', 'No valid test case ID found for deletion.'); return; }
      this.testautomationService.deleteSelectedTestCases({ ids: [testCaseId], isDB2: !!localStorage.getItem('projectIdDb2') }).subscribe({
        next: (res: any) => { if (res && res.operationStatus === 'SUCCESS') { this.testCases.splice(index, 1); this.updateFilteredList(); this.notifyService.notify('success', 'Success', 'Test case deleted successfully.'); if (this.testCases.length === 0) { this.isTestCaseGenerated(); } } else { this.notifyService.onError('Error', 'Failed to delete test case. Please try again.'); } },
        error: () => this.notifyService.onError('Error', 'An error occurred while deleting test case.'),
      });
    });
  }

  deleteSelectedTestCases(): void {
    const selected = this.testCases.filter(tc => tc.selected);
    if (selected.length === 0) { this.notifyService.onWarning('Warning', 'Please select at least one test case to delete.'); return; }
    const message = selected.length === 1 ? 'Are you sure you want to delete this test case?' : `Are you sure you want to delete ${selected.length} selected test cases?`;
    this.dialog.open(DialogComponent, { data: new ConfirmDialogModel('Delete Test Cases', message) }).afterClosed().subscribe(result => {
      if (!result) { this.notifyService.notify('warning', 'Aborted', 'Deletion aborted by user.'); return; }
      const ids = selected.map(tc => this.extractTestCaseId(tc._id)).filter((id): id is string => id !== null);
      if (!ids.length) { this.notifyService.onWarning('Warning', 'No valid test case IDs found for deletion.'); return; }
      this.testautomationService.deleteSelectedTestCases({ ids, isDB2: !!localStorage.getItem('projectIdDb2') }).subscribe({
        next: (res: any) => { if (res && res.operationStatus === 'SUCCESS') { this.testCases = this.testCases.filter(tc => !tc.selected); this.updateFilteredList(); this.selectAll = false; this.notifyService.notify('success', 'Success', `Deleted ${selected.length} test case(s) successfully.`); if (this.testCases.length === 0) { this.isTestCaseGenerated(); } } else { this.notifyService.onError('Error', 'Failed to delete test cases. Please try again.'); } },
        error: () => this.notifyService.onError('Error', 'An error occurred while deleting test cases.'),
      });
    });
  }

  saveToLibrary() {
    const selected = this.testCases.filter(tc => tc.selected);
    if (selected.length === 0) { this.notifyService.onWarning(Literals.Warning, 'Please select at least one test case to save in library.'); return; }
    const ids = selected.map(tc => this.extractTestCaseId(tc._id)).filter((id): id is string => id !== null);
    if (!ids.length) { this.notifyService.onWarning(Literals.Warning, 'No valid test case IDs found for saving.'); return; }
    this.isSavingToLibrary = true;
    this.testautomationService.saveToLibraryTestCases({ ids }).subscribe({
      next: (res: any) => { this.isSavingToLibrary = false; if (res && res.operationStatus === Literals.CAPITAL_SUCCESS) { this.notifyService.notify(Literals.SMALL_SUCCESS, Literals.Success, `Saved ${ids.length} test case(s) to library.`); window.location.reload(); } else { this.notifyService.onError(Literals.Error, (res ? res.operationMessage : undefined) || 'Failed to save selected test cases to library.'); } },
      error: () => { this.isSavingToLibrary = false; this.notifyService.onError(Literals.Error, 'An error occurred while saving test cases to library.'); },
    });
  }

  openReport(testCase: any) {
    const reportId = testCase.last_run_report_id;
    if (reportId) { this.router.navigate(['/report'], { queryParams: { reportId, openTab: 'canvas' } }); }
    else { this.notifyService.onWarning('Warning', 'No report available for this test case.'); }
  }

  openTestDataDialog(testCase: any): void {
    if (!this.hasTestData(testCase.test_data)) { this.notifyService.onWarning('Warning', 'No test data available for this test case.'); return; }
    this.dialog.open(TestDataDialogComponent, {
      width: '500px', maxWidth: '90vw',
      data: { testData: testCase.test_data, testCaseId: testCase.test_case_id || testCase.testcase_id },
      panelClass: 'test-data-dialog-container', backdropClass: 'test-data-dialog-backdrop',
    }).afterClosed().subscribe();
  }

  hasTestData(testData: any[]): boolean { return testData && Array.isArray(testData) && testData.length > 0; }

  applyFilter() { this.updateFilteredList(); }
  updateFilteredList() {
    const q = (this.searchText || '').trim().toLowerCase();
    if (!q) { this.filteredTestCases = this.testCases.slice(); return; }
    this.filteredTestCases = this.testCases.filter(tc =>
      [tc.test_case_id, tc.testcase_id, tc.test_case, tc.description, tc.main_step, tc.suite_name, tc.expected_result, tc.last_run_report_id]
        .map(v => (v || '').toString().toLowerCase()).some(v => v.includes(q))
    );
  }

  editRow(index: number) {
    if (this.editingIndex !== null) { this.notifyService.onWarning('Warning', 'Finish the current edit before editing another row.'); return; }
    const targetRow = this.filteredTestCases[index];
    if (!targetRow) { return; }
    targetRow.selected = true; this.onCheckboxChange();
    this.editingIndex = index; this.editableRow = JSON.parse(JSON.stringify(targetRow));
    this.editableRow.test_stepsText = Array.isArray(this.editableRow.test_steps) ? this.editableRow.test_steps.join('\n') : (this.editableRow.test_stepsText || '');
    this.editableRow.expected_result = this.editableRow.expected_result || '';
  }

  saveEdit(filteredIndex: number) {
    let origIndex = this.testCases.findIndex(tc => tc === this.filteredTestCases[filteredIndex]);
    if (origIndex === -1) {
      const id = this.extractTestCaseId(this.editableRow._id || this.editableRow.testcase_id || this.editableRow.test_case_id);
      origIndex = this.testCases.findIndex(tc => this.extractTestCaseId(tc._id || tc.testcase_id || tc.test_case_id) === id);
    }
    const lines = (this.editableRow.test_stepsText || '').split('\n').map((s: string) => s.trim()).filter((s: string) => s.length > 0);
    this.editableRow.test_steps = lines;
    if (origIndex === -1) { this.filteredTestCases[filteredIndex] = { ...this.filteredTestCases[filteredIndex], ...this.editableRow }; }
    else { this.testCases[origIndex] = { ...this.testCases[origIndex], ...this.editableRow }; this.testCases[origIndex].test_steps = lines; this.testCases[origIndex].expected_result = this.editableRow.expected_result; }
    this.updateFilteredList();
    if (this.editableRow._id) {
      const payload = { ...(origIndex >= 0 ? this.testCases[origIndex] : this.filteredTestCases[filteredIndex]) };
      payload.test_steps = lines; payload.expected_result = this.editableRow.expected_result;
      this.testautomationService.updateTestCase(payload).subscribe({
        next: (res: any) => { if (res && res.operationStatus === 'SUCCESS') { this.notifyService.notify('success', 'Success', 'Test case updated.'); } else { this.notifyService.onWarning('Warning', 'Update returned error'); } },
        error: () => this.notifyService.onError('Error', 'Failed to update test case'),
      });
    } else { this.notifyService.notify('success', 'Success', 'Test case updated locally.'); }
    this.editingIndex = null; this.editableRow = {};
  }

  cancelEdit() { this.editingIndex = null; this.editableRow = {}; }

  getRunButtonText(): string { const n = this.testCases.filter(tc => tc.selected).length; return (n === 0 || n === this.testCases.length) ? 'Run All' : 'Run Selected'; }
  getDeleteButtonText(): string { const n = this.testCases.filter(tc => tc.selected).length; return n === 0 ? 'Delete Selected' : `Delete Selected (${n})`; }
  hasSelectedTestCases(): boolean { return this.testCases.length > 0 && this.testCases.some(tc => tc.selected); }

  private extractTestCaseId(testCaseId: string | { $oid: string }): string | null {
    if (typeof testCaseId === 'string') { return testCaseId; }
    if (testCaseId && typeof testCaseId === 'object' && (testCaseId as any).$oid) { return (testCaseId as any).$oid; }
    return null;
  }

  formatTestData(testData: any[]): string {
    if (!testData || !Array.isArray(testData) || testData.length === 0) { return ''; }
    const formatted: string[] = [];
    testData.forEach(item => { if (item && typeof item === 'object') { Object.keys(item).forEach(key => formatted.push(`${key} = ${item[key]}`)); } });
    return formatted.join('\n');
  }

  onTooltipEnter(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    const tooltip = document.createElement('div');
    tooltip.className = 'custom-tooltip-popup';
    tooltip.textContent = target.getAttribute('data-tooltip') || '';
    tooltip.style.position = 'fixed';
    tooltip.style.zIndex = '999999';
    tooltip.style.maxWidth = '480px';
    document.body.appendChild(tooltip);

    // ── ADD THIS BLOCK after appending so we know the real tooltip dimensions ──
    const tRect = tooltip.getBoundingClientRect();
    let left = rect.left;
    let top  = rect.bottom + 6;

    // clamp right edge
    if (left + tRect.width > window.innerWidth - 16) {
      left = window.innerWidth - tRect.width - 16;
    }

    // flip upward if too close to bottom
    if (top + tRect.height > window.innerHeight - 16) {
      top = rect.top - tRect.height - 6;
    }

    tooltip.style.left = left + 'px';
    tooltip.style.top  = top  + 'px';
    // ── END BLOCK ──
  }

  onTooltipLeave(event: MouseEvent): void {
    const existing = document.querySelectorAll('.custom-tooltip-popup');
    existing.forEach(function(el) { el.remove(); });
  }

}  // ← class closes here
